import Quill from "quill";
import { RefObject, useCallback, useRef, useState } from "react";

import { getHtml } from "./getHtml";

export interface LinkDialogProps {
  key: number;
  isOpen: boolean;
  isEditing: boolean;
  initialText: string;
  initialUrl: string;
  onSave: (text: string, url: string) => void;
  onRemove: () => void;
  onCancel: () => void;
}

export interface UseLinkDialogResult {
  linkDialogProps: LinkDialogProps;
  setupLinkHandlers: (qr: Quill) => void;
}

const defaultUrl = "https://";

export function useRichTextLinkDialog(
  quillRef: RefObject<Quill | null>,
  onChangeRef: RefObject<(value: string) => void>
): UseLinkDialogResult {
  const pendingRangeRef = useRef<{ index: number; length: number } | null>(null);
  const pendingEditNodeRef = useRef<HTMLAnchorElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState("");

  const [url, setUrl] = useState(defaultUrl);
  const [key, setKey] = useState(0);

  const openDialog = useCallback((nextText: string, nextUrl: string, editing: boolean) => {
    setText((nextText || "").trim());
    setUrl((nextUrl || "").trim() || defaultUrl);
    setIsEditing(editing);
    setKey((k) => k + 1);
    setIsOpen(true);
  }, []);

  const handleSave = useCallback(
    (savedText: string, savedUrl: string) => {
      const qr = quillRef.current;
      if (!qr) return;

      if (pendingEditNodeRef.current) {
        const node = pendingEditNodeRef.current;
        node.innerText = savedText || savedUrl;
        node.setAttribute("href", savedUrl);
        pendingEditNodeRef.current = null;
        onChangeRef.current(getHtml(qr));
      } else {
        const range = pendingRangeRef.current ?? { index: Math.max(0, qr.getLength() - 1), length: 0 };
        if (range.length > 0) {
          qr.deleteText(range.index, range.length);
        }
        const insertText = savedText || savedUrl;
        qr.insertText(range.index, insertText, "link", savedUrl);
        // Trailing space so the cursor exits the link format
        qr.insertText(range.index + insertText.length, " ");
        qr.setSelection(range.index + insertText.length + 1, 0);
      }

      setIsOpen(false);
    },
    [quillRef, onChangeRef]
  );

  const handleRemove = useCallback(() => {
    const qr = quillRef.current;
    const node = pendingEditNodeRef.current;
    if (!qr || !node) return;

    const blot = Quill.find(node);
    if (blot && "domNode" in blot) {
      const index = qr.getIndex(blot as Parameters<typeof qr.getIndex>[0]);
      const length = (blot as { length(): number }).length();
      qr.formatText(index, length, "link", false);
    }
    pendingEditNodeRef.current = null;
    onChangeRef.current(getHtml(qr));
    setIsOpen(false);
  }, [quillRef, onChangeRef]);

  const handleCancel = useCallback(() => {
    pendingEditNodeRef.current = null;
    pendingRangeRef.current = null;
    setIsOpen(false);
  }, []);

  const setupLinkHandlers = useCallback(
    (qr: Quill) => {
      const toolbar = qr.getModule("toolbar") as { addHandler(name: string, handler: () => void): void };
      toolbar.addHandler("link", () => {
        const currentQr = quillRef.current;
        if (!currentQr) return;
        const range = currentQr.getSelection();

        // When the cursor is inside an existing link (ql-active), open the edit dialog.
        const format = currentQr.getFormat(range ?? undefined);
        if (format.link) {
          const selectionNode = currentQr.root.ownerDocument.getSelection()?.anchorNode;
          let anchor: HTMLAnchorElement | null = null;
          if (selectionNode instanceof Element) {
            anchor = selectionNode.closest("a[href]");
          } else if (selectionNode?.parentElement) {
            anchor = selectionNode.parentElement.closest("a[href]");
          }
          pendingEditNodeRef.current = anchor;
          pendingRangeRef.current = range ?? null;
          openDialog(anchor?.innerText ?? "", typeof format.link === "string" ? format.link : "", true);
          return;
        }

        pendingRangeRef.current = range ?? { index: Math.max(0, currentQr.getLength() - 1), length: 0 };
        pendingEditNodeRef.current = null;
        openDialog(range && range.length > 0 ? currentQr.getText(range.index, range.length) : "", "", false);
      });

      qr.root.addEventListener(
        "click",
        (e: MouseEvent) => {
          const anchor = (e.target as HTMLElement).closest("a[href]");
          if (anchor instanceof HTMLAnchorElement) {
            e.preventDefault();
            e.stopPropagation();
            pendingEditNodeRef.current = anchor;
            pendingRangeRef.current = null;
            openDialog(anchor.innerText, anchor.getAttribute("href") ?? "", true);
          }
        },
        true
      );
    },
    [quillRef, openDialog]
  );

  const linkDialogProps = {
    key,
    isOpen,
    isEditing,
    initialText: text,
    initialUrl: url,
    onSave: handleSave,
    onRemove: handleRemove,
    onCancel: handleCancel,
  };

  return {
    linkDialogProps,
    setupLinkHandlers,
  };
}
